import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "../lib/api";
import { History, Loader2, Filter } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-width inline Changes tab for a project. Lists every audit entry
 * (what · who · when) with a 7-day filter and a type filter. Backed by
 * the existing GET /api/projects/:id/audit endpoint and audit_logs collection.
 */
export default function ChangesPanel({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d"); // "7d" | "30d" | "all"
  const [kind, setKind] = useState("all"); // "all" | "tasks" | "risks" | "imports"

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${projectId}/audit`)
      .then(({ data }) => setEntries(data || []))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [projectId]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const cutoff =
      range === "7d" ? now - 7 * 24 * 3600 * 1000 :
      range === "30d" ? now - 30 * 24 * 3600 * 1000 :
      0;
    return entries.filter((e) => {
      const t = new Date(e.created_at).getTime();
      if (t < cutoff) return false;
      if (kind === "tasks" && !String(e.action).startsWith("task.")) return false;
      if (kind === "risks" && !String(e.action).startsWith("risk.")) return false;
      if (kind === "imports" && !String(e.action).includes("imported") && !String(e.action).includes("templates_applied")) return false;
      return true;
    });
  }, [entries, range, kind]);

  const counts = useMemo(() => {
    const now = Date.now();
    const last7 = entries.filter((e) => new Date(e.created_at).getTime() > now - 7 * 24 * 3600 * 1000);
    return { total: entries.length, last7: last7.length };
  }, [entries]);

  return (
    <div className="pc-card p-6" data-testid="changes-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80">
            <History size={12}/> Change log
          </div>
          <div className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: "Outfit" }}>
            Every change on this project
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {counts.last7} change{counts.last7 === 1 ? "" : "s"} in the last 7 days · {counts.total} total.
            Who edited what, and when — in reverse chronological order.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="changes-range">
            {[{k:"7d",l:"7 days"},{k:"30d",l:"30 days"},{k:"all",l:"All time"}].map((o)=> (
              <button key={o.k} onClick={() => setRange(o.k)} data-testid={`changes-range-${o.k}`}
                className={`px-3 py-1.5 text-xs ${range===o.k?"bg-blue-600 text-white":"text-slate-300 hover:bg-slate-800"}`}>
                {o.l}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="changes-kind">
            {[{k:"all",l:"All"},{k:"tasks",l:"Tasks"},{k:"risks",l:"Risks"},{k:"imports",l:"Imports"}].map((o)=> (
              <button key={o.k} onClick={() => setKind(o.k)} data-testid={`changes-kind-${o.k}`}
                className={`px-3 py-1.5 text-xs ${kind===o.k?"bg-blue-600 text-white":"text-slate-300 hover:bg-slate-800"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8" data-testid="changes-loading">
          <Loader2 className="animate-spin" size={16}/> Loading history…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-sm" data-testid="changes-empty">
          <Filter className="inline-block mb-2 text-slate-600" size={20}/>
          <div>Nothing recorded in this window.</div>
          <div className="text-xs mt-1">Try widening the range or clearing the type filter.</div>
        </div>
      ) : (
        <div className="divide-y divide-slate-800/60" data-testid="changes-list">
          {filtered.map((e) => <ChangeRow key={e.id} e={e}/>)}
        </div>
      )}
    </div>
  );
}

function ChangeRow({ e }) {
  const act = String(e.action || "");
  const [entity, verb] = act.split(".");
  const tone =
    verb === "deleted" ? "border-rose-500/50 bg-rose-500/5" :
    verb === "created" ? "border-emerald-500/50 bg-emerald-500/5" :
    verb === "imported" || verb === "templates_applied" ? "border-blue-500/50 bg-blue-500/5" :
    "border-slate-700 bg-slate-900/30";

  const label = (e.meta?.name) || (e.meta?.description) || e.entity_id;
  const changes = e.meta?.changes;
  const ts = formatFull(e.created_at);

  return (
    <div className={`py-3 pl-3 border-l-2 ${tone}`} data-testid={`change-row-${e.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-200 leading-snug">
          <span className="font-semibold text-white">{e.actor_name || "Someone"}</span>{" "}
          <span className="text-slate-400">{verb || act}</span>{" "}
          <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-1 border border-slate-700 rounded px-1 py-0.5">{entity || "entity"}</span>{" "}
          <span className="text-slate-100">{label || "—"}</span>
          {e.meta?.count != null && <span className="text-slate-400"> · {e.meta.count} rows</span>}
        </div>
        <div className="text-[11px] text-slate-500 mono whitespace-nowrap" title={e.created_at}>{ts}</div>
      </div>
      {changes && Object.keys(changes).length > 0 && (
        <div className="mt-2 ml-1 space-y-0.5">
          {Object.entries(changes).slice(0, 8).map(([k, v]) => (
            <div key={k} className="text-[12px] text-slate-400 leading-snug">
              <span className="text-slate-500">{k}:</span>{" "}
              <span className="text-rose-300 line-through">{fmt(v.from)}</span>{" "}
              <span className="text-slate-500">→</span>{" "}
              <span className="text-emerald-300">{fmt(v.to)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "∅";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return s.length > 60 ? s.slice(0, 58) + "…" : s;
}

function formatFull(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
